import { computed, nextTick, ref, shallowRef } from 'vue';
import { defineStore } from 'pinia';
import type { FabricImage } from '@/fabric';
import { activeFabricCanvas } from '@/fabric/canvas';
import { ImageService } from '@/services/ImageService';
import { useViewTransition } from '@/composables/useViewTransition';

function clamp (min: number, value: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export const backgroundImageScaleRoundFactor = 1000;

export const useBackgroundStore = defineStore('background', () => {
  const canvasWidth = ref(1080);
  const canvasHeight = ref(1440);

  const backgroundImage = shallowRef<FabricImage | null>(null);
  const backgroundImageScale = ref(1);
  const minBackgroundImageScale = ref(1);
  const backgroundImageIsVector = ref<boolean | null>(null);
  const imagePickerTriggerCount = ref(0);

  function triggerImagePicker () {
    imagePickerTriggerCount.value += 1;
  }

  const backgroundImageFile = shallowRef<File | null>(null);

  const hasBackgroundImage = computed(() => backgroundImage.value !== null);
  const hasBackgroundImageQualityProblem = computed(() => {
    if (!backgroundImage.value) {
      return false;
    }
    if (backgroundImageIsVector.value) {
      return false;
    }
    return backgroundImageScale.value > 1;
  });

  const maxBackgroundImageScale = computed(() => {
    return backgroundImageIsVector.value
      ? Math.ceil(3 * minBackgroundImageScale.value * backgroundImageScaleRoundFactor) / backgroundImageScaleRoundFactor
      : Math.max(1, minBackgroundImageScale.value);
  });

  function enforceBackgroundImageCoversCanvas (backgroundImageObject: FabricImage) {
    const scaledWidth = backgroundImageObject.getScaledWidth();
    const scaledHeight = backgroundImageObject.getScaledHeight();

    backgroundImageObject.left = clamp(
      canvasWidth.value - scaledWidth / 2,
      backgroundImageObject.left ?? canvasWidth.value / 2,
      scaledWidth / 2,
    );

    backgroundImageObject.top = clamp(
      canvasHeight.value - scaledHeight / 2,
      backgroundImageObject.top ?? canvasHeight.value / 2,
      scaledHeight / 2,
    );
  }

  async function removeBackgroundImage (viewTransitionEnabled: boolean) {
    return useViewTransition(async () => {
      if (backgroundImage.value && activeFabricCanvas) {
        activeFabricCanvas.remove(backgroundImage.value);
        activeFabricCanvas.discardActiveObject();
        activeFabricCanvas.renderAll();
      }

      backgroundImage.value = null;
      backgroundImageFile.value = null;
      backgroundImageScale.value = 1;
      minBackgroundImageScale.value = 1;
      backgroundImageIsVector.value = null;
      await nextTick();
    }, viewTransitionEnabled);
  }

  function setBackgroundImageScale (nextScale: number) {
    if (!activeFabricCanvas || !backgroundImage.value) {
      return;
    }

    const maxScale = Math.ceil(3 * minBackgroundImageScale.value * backgroundImageScaleRoundFactor) / backgroundImageScaleRoundFactor;
    const clampedScale = clamp(minBackgroundImageScale.value, nextScale, maxScale);

    backgroundImage.value.scale(clampedScale);
    enforceBackgroundImageCoversCanvas(backgroundImage.value);

    activeFabricCanvas.discardActiveObject();
    activeFabricCanvas.renderAll();

    backgroundImageScale.value = clampedScale;
  }

  async function setBackgroundImage (
    file: File,
    imageBase64: string,
  ) {
    if (!activeFabricCanvas) {
      throw new Error('Fabric canvas is not ready yet.');
    }

    useViewTransition(async () => {
      await removeBackgroundImage(false);

      const backgroundImageObject = await ImageService.createFabricImageFromUrl(imageBase64);

      backgroundImageObject.originX = 'center';
      backgroundImageObject.originY = 'center';
      backgroundImageObject.left = canvasWidth.value / 2;
      backgroundImageObject.top = canvasHeight.value / 2;
      backgroundImageObject.selectable = true;
      backgroundImageObject.evented = true;
      backgroundImageObject.hoverCursor = 'grab';
      backgroundImageObject.moveCursor = 'grabbing';
      backgroundImageObject.hasControls = false;
      backgroundImageObject.lockRotation = true;
      backgroundImageObject.lockScalingFlip = true;
      backgroundImageObject.on('moving', () => {
        enforceBackgroundImageCoversCanvas(backgroundImageObject);
      });

      const coverScale = Math.max(
        canvasWidth.value / (backgroundImageObject.width ?? canvasWidth),
        canvasHeight.value / (backgroundImageObject.height ?? canvasHeight),
      );

      const fileNameLower = file.name.toLowerCase();
      const isVector = file.type === 'image/svg+xml' || fileNameLower.endsWith('.svg');

      backgroundImageObject.scale(coverScale);
      enforceBackgroundImageCoversCanvas(backgroundImageObject);

      activeFabricCanvas.add(backgroundImageObject);
      activeFabricCanvas.setActiveObject(backgroundImageObject);
      activeFabricCanvas.renderAll();

      backgroundImage.value = backgroundImageObject;
      backgroundImageFile.value = file;
      minBackgroundImageScale.value = coverScale;
      backgroundImageScale.value = coverScale;
      backgroundImageIsVector.value = isVector;
      await nextTick();
    });
  }

  return {
    canvasWidth,
    canvasHeight,
    backgroundImage,
    backgroundImageFile,
    backgroundImageScale,
    minBackgroundImageScale,
    maxBackgroundImageScale,
    backgroundImageIsVector,
    hasBackgroundImage,
    hasBackgroundImageQualityProblem,
    imagePickerTriggerCount,
    triggerImagePicker,
    setBackgroundImage,
    setBackgroundImageScale,
    removeBackgroundImage,
    enforceBackgroundImageCoversCanvas,
  };
});
